import { ShieldCheck, ShoppingBag } from "lucide-react";
import { ButtonLink, Drawer, EmptyState, Skeleton } from "@/components/ui";
import { VerifiedBadge } from "@/components/ui";
import { formatNaira } from "@/lib/money";
import { useUI } from "@/stores/ui";
import { useCart } from "./api";
import { CartLineRow } from "./CartLineRow";

/** Slide-in cart from the navbar icon. */
export function CartDrawer() {
  const open = useUI((s) => s.cartOpen);
  const setOpen = useUI((s) => s.setCartOpen);
  const cart = useCart(open);
  const data = cart.data;
  const close = () => setOpen(false);

  return (
    <Drawer
      open={open}
      onClose={close}
      title={`Your cart${data?.item_count ? ` (${data.item_count})` : ""}`}
      footer={
        data && data.item_count > 0 ? (
          <div className="flex w-full flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-ink-muted">Subtotal</span>
              <span className="tabular text-xl font-bold">{formatNaira(data.subtotal)}</span>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-ink-muted">
              <ShieldCheck className="size-3.5 text-cyan" aria-hidden /> Delivery and RealitySafe fee are calculated at checkout.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <ButtonLink to="/cart" variant="ghost" onClick={close}>
                View cart
              </ButtonLink>
              <ButtonLink to="/checkout" onClick={close}>
                Checkout
              </ButtonLink>
            </div>
          </div>
        ) : undefined
      }
    >
      {cart.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : !data || data.item_count === 0 ? (
        <EmptyState
          icon={<ShoppingBag />}
          title="Your cart is empty"
          description="Find something you'll love. RealitySafe protects every online payment."
          action={
            <ButtonLink to="/search" onClick={close}>
              Start shopping
            </ButtonLink>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          {data.groups.map((g) => (
            <section key={g.vendor.id} aria-label={g.vendor.store_name}>
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                {g.vendor.store_name} {g.vendor.is_verified && <VerifiedBadge compact />}
              </h3>
              <ul className="divide-y divide-line">
                {g.items.map((line) => (
                  <CartLineRow key={line.id} line={line} compact />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Drawer>
  );
}
